// PHASE_3 / B1-frontend — typed fetch wrappers for the asset-custody endpoints.
//
// Explicit adapter signatures below describe each consumed wire contract.
// Generated OpenAPI paths exist; request/response metadata varies by endpoint,
// so these casts retain the local DTO boundary.
// Every fetch
// unwraps the spec-58 envelope through `@/api/errors`; lists go through the
// keyset shape `{ items, nextCursor }`.
//
// MONEY: every money field crosses the edge as an integer minor-unit STRING
// (`*_minor_units`) paired with an ISO-4217 `currency` on the same row. NOTHING
// here carries money as a number — the <Money> component is the ONLY place a
// figure reassembles into a localized currency string (Number() at that display
// boundary only; never a float parse / float math here).
//
// IDEMPOTENCY: every retryable custody mutation owns an immutable key for one
// normalized intent. The modal retains that key through ambiguous response loss
// so the server can replay the original semantic action rather than append a
// second custody ledger effect.

import type {
  AssetCondition,
  AssetForceEndReason,
  AssetOwnershipKind,
  AssetReturnReason,
  AssetWriteOffReason,
  IsgHandoverMetadata,
} from "@novemai/asset-custody-sdk";

import { getAccessToken, platformGraphBaseUrl, platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import type { KeysetPage } from "@/api/keyset";

// --------------------------------------------------------------------------
// Wire row shapes (mirror services/platform-graph/src/routes/assets.ts DTOs)
// --------------------------------------------------------------------------

/**
 * Kira bloğu — `entity_asset.lease_details` (SEM-6952).
 *
 * İç anahtarlar camelCase'tir, üst düzey varlık alanları snake_case. Bu karışım
 * kasıtlıdır: blok SDK'nın kendi şeklinde saklanır ve uyum süpürgesi o şekli
 * okur. Çevirmek, kenar ile tek okuyucu arasına ikinci bir eşleme koyardı.
 *
 * `monthlyRateMinorUnits` kenardan tam sayı METİN geçer (para invaryantı),
 * sunucuda sayıya çevrilip öyle saklanır — geri de metin olarak döner.
 */
export interface LeaseDetailsBlock {
  readonly lessorName: string;
  readonly leaseContractRef: string;
  readonly leaseStartAt: string;
  readonly leaseEndAt: string;
  readonly monthlyRateMinorUnits: string;
  readonly currency: string;
  readonly returnTermsSummary?: string;
  readonly renewalPolicy: "auto_renew" | "requires_confirmation" | "ends";
}

/** Bir aracın sürücü ataması. `toDate` yoksa atama açıktır. */
export interface VehicleDriverAssignment {
  readonly employeeId: string;
  readonly fromDate: string;
  readonly toDate?: string;
}

/**
 * Araç bloğu — `entity_asset.vehicle_details` (SEM-6952). Blok BÜTÜNÜYLE
 * pii:true'dur (plaka KVKK'ya göre kişisel veridir), bu yüzden liste yanıtında
 * YOKTUR; yalnız tek satır okumaları taşır.
 */
export interface VehicleDetailsBlock {
  readonly licensePlate: string;
  readonly trafikSigortasiExpiresAt: string;
  readonly muayeneExpiresAt: string;
  readonly hgsOgsRef?: string;
  readonly fuelCardId?: string;
  readonly insurancePolicyRef?: string;
  readonly emissionClass?: "EURO_4" | "EURO_5" | "EURO_6" | "EURO_6D" | "electric" | "hybrid";
  readonly driverAssignments?: readonly VehicleDriverAssignment[];
}

/** An asset (account) row from GET /v1/assets. Money is a minor-unit STRING. */
export interface AssetRow {
  readonly asset_id: string;
  readonly tenant_id: string;
  /** null: katalog dışı kayıt (SEM-6358) — adı brand_model taşır. */
  readonly model_id: string | null;
  /** Serbest metin marka/model. Katalog satırı olmayan varlığın adı. */
  readonly brand_model: string | null;
  readonly serial_number: string | null;
  readonly asset_tag: string | null;
  readonly ownership_kind: string | null;
  readonly state: string;
  readonly location_id: string | null;
  /**
   * Server-resolved location display name (mirrors the assignment
   * employee_name join). null when the asset has no location, when the id is
   * unresolvable, or on a mutation response — GET /v1/assets is the only
   * surface that enriches it. The inventory column and the detail row render
   * the absent-marker for null; neither ever renders location_id.
   */
  readonly location_name: string | null;
  /**
   * Server-resolved catalog model name (entity_asset_model.name). Same contract
   * as location_name: null when the model is unresolvable in this tenant. The
   * inventory Model column and the detail Model row render this; neither ever
   * renders model_id, which is a UUID no human recognizes.
   */
  readonly model_name: string | null;
  readonly condition: string | null;
  /** Integer minor units as a STRING (or null when gifted/no-cost). Never a float. */
  readonly acquired_cost_minor_units: string | null;
  readonly acquired_currency: string | null;
  readonly acquired_at: string | null;
  readonly notes: string | null;
  readonly warranty_expires_at: string | null;
  readonly insurance_ref: string | null;
  readonly purchase_order_ref: string | null;
  readonly linked_device_id: string | null;
  readonly linked_corp_card_id: string | null;
  readonly lease_details: LeaseDetailsBlock | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

/**
 * Tek satır okumasının şekli — `GET /v1/assets/:asset_id` ve `PATCH` bunu döner.
 * Listeden TEK alanla ayrılır ve o alan ayrımın sebebidir: `vehicle_details`
 * pii:true'dur, ortak izdüşüme koymak envanterin her sayfasında plaka kolonu
 * olmayan bir ekrana 500 plaka taşırdı.
 */
export interface AssetDetailRow extends AssetRow {
  readonly vehicle_details: VehicleDetailsBlock | null;
}

/** An assignment (transaction, append-only) row from GET /v1/assignments. */
export interface AssignmentRow {
  readonly assignment_id: string;
  readonly tenant_id: string;
  readonly asset_id: string;
  readonly employee_id: string;
  /**
   * SEM-301 display-name join — the custodian's name, resolved server-side from
   * employee_id. null when unresolved (out-of-tenant) or PII-redacted (the
   * subject was erased). The asset-detail history table renders this with a
   * short-id fallback.
   */
  readonly employee_name: string | null;
  /**
   * The asset model's KKD (Kanun 6331) flag, joined server-side from
   * entity_asset_model — this row is the sign modal's only read of the asset.
   * An asset outside the catalogue reads false, matching the server's own sign
   * gate; null is an unresolved asset (SEM-8032).
   */
  readonly isg_ppe_mandatory: boolean | null;
  /**
   * The device this row binds, joined server-side from the asset (the row
   * carries only asset_id). `model_name` is the catalogue name, or the
   * free-text brand_model of an off-catalogue asset. Optional because only the
   * list read resolves them; a mutation response leaves them null.
   */
  readonly asset_tag?: string | null;
  readonly serial_number?: string | null;
  readonly model_name?: string | null;
  readonly state: string;
  readonly assigned_at: string | null;
  readonly expected_return_at: string | null;
  readonly condition_at_handover: string | null;
  readonly returned_at: string | null;
  readonly condition_at_return: string | null;
  readonly reason_for_end: string | null;
}

/** The signed handover receipt (artifact, append-only) the sign flow produces. */
export interface HandoverRecordRow {
  readonly handover_record_id: string;
  readonly assignment_id: string;
  readonly document_id: string;
  readonly terms_template_id: string;
  readonly terms_template_version: string;
  readonly state: string;
  readonly signed_at: string | null;
}

// --------------------------------------------------------------------------
// Mutation drafts (the forms' validated output)
// --------------------------------------------------------------------------

/**
 * Spec 61 enums come from the SDK (SEM-6956), which is also where the route's
 * `z.enum()` reads them. They used to be hand-copied here with a "MUST match
 * routes/assets.ts" comment above them, and the comment was the whole
 * enforcement: a prior complete/damaged/missing_parts condition set matched no
 * server enum at all, so every return 400'd — including on the form's own
 * defaults, the most common path. A comment cannot fail a build.
 */
export type { AssetCondition, AssetOwnershipKind } from "@novemai/asset-custody-sdk";

/** POST /v1/assets body — register a new asset. Money is an int-string. */
export interface CreateAssetDraft {
  /** Opsiyonel (SEM-6358): katalog bir kolaylıktır, bir kapı değil. */
  readonly model_id?: string;
  readonly brand_model?: string;
  readonly serial_number?: string;
  readonly asset_tag?: string;
  readonly ownership_kind?: AssetOwnershipKind;
  /** YYYY-MM-DD. */
  readonly acquired_at: string;
  /** Integer minor units as a STRING (the form never collects a float). */
  readonly acquired_cost_minor_units?: string;
  readonly acquired_currency?: string;
  readonly location_id?: string;
  readonly condition?: AssetCondition;
  readonly notes?: string;
  readonly warranty_expires_at?: string;
  readonly insurance_ref?: string;
  readonly purchase_order_ref?: string;
  readonly linked_device_id?: string;
  readonly linked_corp_card_id?: string;
  readonly lease_details?: LeaseDetailsBlock;
  readonly vehicle_details?: VehicleDetailsBlock;
}

/**
 * PATCH /v1/assets/:asset_id gövdesi — düzeltilebilir alan kümesi.
 *
 * Eksik olanlar KASITLIDIR ve sunucu şeması `.strict()` olduğu için gönderilmeleri
 * görünür bir 400'dür, sessiz bir düşme değil: `state`'in doğrulanmış geçiş
 * tablosu vardır, `acquired_*` amortismanın ve kesintinin tabanıdır,
 * `ownership_kind` kira bloğunun gerekli olup olmadığını belirler.
 *
 * `model_id` yalnız varlık hiç kimsenin eline geçmemişken düzeltilebilir
 * (`state='in_stock'` ve sıfır zimmet satırı) — bu yüzden `null` alamaz.
 * Diğer alanlarda `null` "temizle" demektir; alanı hiç göndermemek
 * "dokunma" demektir. İkisi farklı isteklerdir.
 */
export interface PatchAssetDraft {
  readonly model_id?: string;
  readonly brand_model?: string | null;
  readonly asset_tag?: string | null;
  readonly serial_number?: string | null;
  readonly location_id?: string | null;
  readonly condition?: AssetCondition | null;
  readonly notes?: string | null;
  readonly warranty_expires_at?: string | null;
  readonly insurance_ref?: string | null;
  readonly purchase_order_ref?: string | null;
  readonly linked_device_id?: string | null;
  readonly linked_corp_card_id?: string | null;
  readonly lease_details?: LeaseDetailsBlock | null;
  readonly vehicle_details?: VehicleDetailsBlock | null;
}

/** signature_method enum the assign + sign flows accept (spec 61). */
export type SignatureMethod =
  | "in_app_acknowledgment"
  | "esign"
  | "kep"
  | "mobile_signature_tr"
  | "wet_signature_scanned";

/** POST /v1/assets/:asset_id/assign body. */
export interface AssignDraft {
  readonly employee_id: string;
  readonly vehicle_reservation_id?: string;
  readonly signature_method: SignatureMethod;
  readonly expected_return_at?: string;
  readonly condition_at_handover?: AssetCondition;
}

/** POST /v1/assignments/:assignment_id/sign body (employee_self custodian sign). */
export interface SignDraft {
  readonly signature_method: SignatureMethod;
  /** Server-prepared document or uploaded wet-signed scan. */
  readonly document_id: string;
  /** 64-hex sha256 content hash of the terms template. */
  readonly terms_template_id: string;
  readonly terms_template_version: string;
  /** ≤5000 chars. */
  readonly terms_snapshot: string;
  readonly countersigned_by_employee_id?: string;
  readonly ip_address_at_sign?: string;
  readonly crypto_timestamp?: string;
  readonly isg_metadata?: IsgHandoverMetadata;
}

/** Named here for the drafts below; the set itself lives in the SDK. */
export type { AssetReturnReason as ReturnReason } from "@novemai/asset-custody-sdk";

/** POST /v1/assignments/:assignment_id/return body. */
export interface ReturnDraft {
  readonly condition_at_return: AssetCondition;
  readonly reason_for_end: AssetReturnReason;
  readonly returned_to?: string;
  readonly notes?: string;
}

/**
 * POST /v1/assignments/:assignment_id/force-end body. Administrative terminal
 * exit for an assignment that will NOT complete normally (an abandoned
 * pending_handover the custodian never signed, or an active binding whose
 * custodian departed without returning).
 */
export interface ForceEndDraft {
  readonly reason_for_end: AssetForceEndReason;
  readonly notes?: string;
}

/** POST /v1/assets/:asset_id/write-off body. */
export interface WriteOffDraft {
  readonly reason: AssetWriteOffReason;
  /** SEM-6359 — required by the route: who took the scrap decision. */
  readonly decided_by_employee_id: string;
  /** SEM-6359 — required by the route: the basis for the decision. */
  readonly explanation: string;
  /** The day the decision was taken, when known. Never the entry date. */
  readonly decision_date?: string;
  readonly evidence_document_ids?: readonly string[];
}

/** One row of GET /v1/assets/:asset_id/write-off-decisions (SEM-6359). */
export interface WriteOffDecisionRow {
  readonly decision_id: string;
  readonly asset_id: string;
  readonly reason: AssetWriteOffReason;
  readonly state: "requested" | "applied" | "rejected";
  readonly decided_by_employee_id: string;
  readonly decided_by_name: string | null;
  readonly explanation: string;
  readonly decision_date: string | null;
  readonly evidence_document_ids: readonly string[];
  readonly approval_request_id: string | null;
  readonly requested_by_employee_id: string;
  readonly requested_by_name: string | null;
  readonly requested_at: string;
  readonly applied_at: string | null;
}

/**
 * Write-off result — the route returns 200 (asset flipped to written_off) OR
 * 202 (over-threshold → pending approval, asset UNCHANGED, approval_request_id
 * present). The caller surfaces the pending-approval state when present.
 */
export interface WriteOffResult {
  readonly asset: AssetRow;
  /** Set only on the 202 pending-approval branch; the asset is unchanged then. */
  readonly approval_request_id?: string;
}

// --------------------------------------------------------------------------
// List query params
// --------------------------------------------------------------------------

export interface AssetListFilters {
  readonly state?: string;
  readonly location_id?: string;
  readonly model_id?: string;
  /** Kategori id'si. Sunucu yüklemi model üzerinden yarı-join'dir; ekran
   *  yalnız id taşır, slug taşımaz (SEM-6952). */
  readonly category_id?: string;
}

export interface AssignmentListFilters {
  readonly asset_id?: string;
  /**
   * IGNORED for employee_self callers — the server pins self-tier callers to
   * their OWN rows (Zimmetlerim). Effective only for manager/admin tiers.
   */
  readonly employee_id?: string;
  readonly state?: string;
  /**
   * Server-authoritative "my rows" selector — narrows to the caller's own
   * assignments at EVERY tier. Zimmetlerim passes it because the self-tier pin
   * alone left a manager looking at the subtree's zimmets (SEM-6054).
   */
  readonly mine?: boolean;
}

// --------------------------------------------------------------------------
// Fetchers use the explicit wire signatures below.
// Kategori süzgeci `category_id` ile gider (SEM-6952). Eski `category` METİN
// parametresi hiç var olmadı: kategori bugün `entity_asset_category` satırıdır.
// --------------------------------------------------------------------------

type AssetsGet = (
  path: "/v1/assets",
  init: {
    params: {
      query: {
        state?: string;
        location_id?: string;
        model_id?: string;
        category_id?: string;
        cursor?: string;
      };
    };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<AssetRow>>>;

/** Fetch one keyset page of the asset inventory (filters: state/location/model/category). */
export async function fetchAssetsPage(
  cursor: string | null,
  filters: AssetListFilters = {},
): Promise<KeysetPage<AssetRow>> {
  const get = platformGraph.GET as unknown as AssetsGet;
  const query: {
    state?: string;
    location_id?: string;
    model_id?: string;
    category_id?: string;
    cursor?: string;
  } = {};
  if (filters.state !== undefined) query.state = filters.state;
  if (filters.location_id !== undefined) query.location_id = filters.location_id;
  if (filters.model_id !== undefined) query.model_id = filters.model_id;
  if (filters.category_id !== undefined) query.category_id = filters.category_id;
  if (cursor !== null) query.cursor = cursor;
  return unwrapEnvelope(get("/v1/assets", { params: { query } }));
}

type AssetByIdGet = (
  path: "/v1/assets/{asset_id}",
  init: { params: { path: { asset_id: string } } },
) => Promise<EnvelopedFetchResult<{ asset: AssetDetailRow }>>;

/**
 * Tek varlığı kimliğinden okur. Detay rotası ve düzenleme ön dolgusu bunu ister:
 * listeden aramak, satır üçüncü sayfadaysa çalışmaz. Sunucu `employee_self`
 * çağırana 403 değil 404 döner — listeleyemediği kimlik uzayını sızdırmamak için.
 */
export async function fetchAssetById(assetId: string): Promise<AssetDetailRow> {
  const get = platformGraph.GET as unknown as AssetByIdGet;
  const result = await unwrapEnvelope(
    get("/v1/assets/{asset_id}", { params: { path: { asset_id: assetId } } }),
  );
  return result.asset;
}

/** One pickable value for an id-typed inventory filter (GET /v1/assets takes
 *  location_id / model_id / category_id as UUIDs — the screen must offer, never
 *  ask for, them). */
export interface AssetFilterOptions {
  readonly locations: readonly { readonly location_id: string; readonly name: string }[];
  /**
   * Model seçenekleri. `category_id` + `lifecycle_state` taşır çünkü aynı küme
   * hem süzgeç çubuğunu hem VARLIK FORMUNU besler ve ikisi farklı alt küme
   * ister: form arşivli modeli sunmaz ve seçilen modelin kategorisini çözerek
   * araç bloğunu açar, süzgeç ise arşivli modelleri korur (o modele kayıtlı
   * varlıklar envanterde durmaya devam eder).
   */
  readonly models: readonly {
    readonly model_id: string;
    readonly name: string;
    readonly category_id: string;
    readonly lifecycle_state: string;
  }[];
  /** Kategori seçenekleri etiket için üç alan taşır — `resolveCategoryLabel`
   *  hangisinin çizileceğini seçer (bkz. category-label.ts). `detail_profile`
   *  formun araç bloğunu açan tek anahtardır; kategori ADI asla eşleştirilmez. */
  readonly categories: readonly {
    readonly category_id: string;
    readonly slug: string;
    readonly name: string;
    readonly is_system: boolean;
    readonly is_renamed: boolean;
    readonly detail_profile: string | null;
  }[];
}

type FilterOptionsGet = (
  path: "/v1/assets/filter-options",
) => Promise<EnvelopedFetchResult<AssetFilterOptions>>;

/** The location/model option sets for the inventory filter bar. Same permission
 *  gate as the list itself; an employee_self caller gets empty sets. */
export async function fetchAssetFilterOptions(): Promise<AssetFilterOptions> {
  const get = platformGraph.GET as unknown as FilterOptionsGet;
  return unwrapEnvelope(get("/v1/assets/filter-options"));
}

type AssignmentsGet = (
  path: "/v1/assignments",
  init: {
    params: {
      query: {
        asset_id?: string;
        employee_id?: string;
        state?: string;
        mine?: "true";
        cursor?: string;
      };
    };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<AssignmentRow>>>;

/**
 * Fetch one keyset page of assignments. For the admin asset-detail history pass
 * `{ asset_id }`; for the employee Zimmetlerim pass `{ mine: true }`.
 */
export async function fetchAssignmentsPage(
  cursor: string | null,
  filters: AssignmentListFilters = {},
): Promise<KeysetPage<AssignmentRow>> {
  const get = platformGraph.GET as unknown as AssignmentsGet;
  const query: {
    asset_id?: string;
    employee_id?: string;
    state?: string;
    mine?: "true";
    cursor?: string;
  } = {};
  if (filters.asset_id !== undefined) query.asset_id = filters.asset_id;
  if (filters.employee_id !== undefined) query.employee_id = filters.employee_id;
  if (filters.state !== undefined) query.state = filters.state;
  // Literal string, not a boolean: the server parses `mine` as the TEXT "true"
  // so that "false" cannot coerce to Boolean("false") === true. Omitted rather
  // than sent as "false" — absent and negative mean the same thing here.
  if (filters.mine === true) query.mine = "true";
  if (cursor !== null) query.cursor = cursor;
  return unwrapEnvelope(get("/v1/assignments", { params: { query } }));
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

/** Existing state-transition callers retain a fresh key when they have no retry UI. */
function idempotencyHeader(idempotencyKey: string = crypto.randomUUID()): Record<string, string> {
  return { "x-idempotency-key": idempotencyKey };
}

type AssetPost = (
  path: "/v1/assets",
  init: { body: CreateAssetDraft; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ asset: AssetRow }>>;

/** Registers a new asset. Carries `x-idempotency-key` (spec `idempotency:
 * required`) so a double-submit / network retry replays the original asset
 * instead of appending a duplicate row. */
export async function createAsset(
  draft: CreateAssetDraft,
  idempotencyKey: string,
): Promise<AssetRow> {
  const post = platformGraph.POST as unknown as AssetPost;
  const result = await unwrapEnvelope(
    post("/v1/assets", { body: draft, headers: idempotencyHeader(idempotencyKey) }),
  );
  return result.asset;
}

type AssetPatch = (
  path: "/v1/assets/{asset_id}",
  init: { params: { path: { asset_id: string } }; body: PatchAssetDraft },
) => Promise<EnvelopedFetchResult<{ asset: AssetDetailRow }>>;

/**
 * Kaydı düzeltir. `x-idempotency-key` TAŞIMAZ, kasıtlı: PATCH adlandırdığı her
 * alanın tam istenen değerini taşır, tekrarı aynı satır durumuna varır. POST
 * anahtar ister, çünkü kaybolan bir 201 ikinci bir varlık basardı.
 */
export async function patchAsset(
  assetId: string,
  draft: PatchAssetDraft,
): Promise<AssetDetailRow> {
  const patch = platformGraph.PATCH as unknown as AssetPatch;
  const result = await unwrapEnvelope(
    patch("/v1/assets/{asset_id}", {
      params: { path: { asset_id: assetId } },
      body: draft,
    }),
  );
  return result.asset;
}

type AssignPost = (
  path: "/v1/assets/{asset_id}/assign",
  init: {
    params: { path: { asset_id: string } };
    body: AssignDraft;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ assignment: AssignmentRow; handover_terms_published?: boolean }>>;

export interface AssignResult {
  readonly assignment: AssignmentRow;
  /**
   * False when the tenant has no published handover terms, so the custodian
   * cannot sign yet. Only an explicit `false` warns: a replay of a response
   * stored before the field existed omits it, and that is not a warning.
   */
  readonly handoverTermsPublished: boolean;
}

/** Binds custody to an employee (pending_handover). Carries `x-idempotency-key`
 * (spec `idempotency: required`) so a double-submit / network retry replays
 * the original assignment instead of appending a duplicate row. */
export async function assignAsset(
  assetId: string,
  draft: AssignDraft,
  idempotencyKey: string,
): Promise<AssignResult> {
  const post = platformGraph.POST as unknown as AssignPost;
  const result = await unwrapEnvelope(
    post("/v1/assets/{asset_id}/assign", {
      params: { path: { asset_id: assetId } },
      body: draft,
      headers: idempotencyHeader(idempotencyKey),
    }),
  );
  return {
    assignment: result.assignment,
    handoverTermsPublished: result.handover_terms_published !== false,
  };
}

/** Evidence is produced by the custodian-scoped document service. */
export interface PreparedHandover {
  readonly document_id: string;
  readonly terms_template_id: string;
  readonly terms_template_version: string;
  readonly terms_snapshot: string;
  readonly sha256: string;
  readonly mime_type: "application/pdf";
  readonly bytes: number;
  readonly document_url: string;
  readonly evidence_kind: "prepared_pdf" | "wet_scan";
}

type PreparePost = (path: "/v1/assignments/{assignment_id}/handover/prepare" | "/v1/assignments/{assignment_id}/handover/signed-scan",
  init: { params: { path: { assignment_id: string } }; body: Record<string, unknown>; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ handover: PreparedHandover }>>;

export async function prepareAssignmentHandover(assignmentId: string, idempotencyKey: string): Promise<PreparedHandover> {
  const result = await unwrapEnvelope((platformGraph.POST as unknown as PreparePost)(
    "/v1/assignments/{assignment_id}/handover/prepare", {
      params: { path: { assignment_id: assignmentId } }, body: {}, headers: idempotencyHeader(idempotencyKey),
    }));
  return result.handover;
}

export async function uploadHandoverSignedScan(assignmentId: string, pdfBase64: string, idempotencyKey: string): Promise<PreparedHandover> {
  const result = await unwrapEnvelope((platformGraph.POST as unknown as PreparePost)(
    "/v1/assignments/{assignment_id}/handover/signed-scan", {
      params: { path: { assignment_id: assignmentId } },
      body: { pdf_base64: pdfBase64, mime_type: "application/pdf", attests_signed_handover: true },
      headers: idempotencyHeader(idempotencyKey),
    }));
  return result.handover;
}

/** Without `documentId` the server returns the assignment's prepared handover
 * PDF; the my-assets list row carries no document id. */
export async function downloadHandoverDocument(assignmentId: string, documentId: string | null, filename: string): Promise<void> {
  const token = getAccessToken();
  const headers: Record<string, string> = { accept: "application/pdf" };
  if (token) headers["authorization"] = `Bearer ${token}`;
  const query = documentId === null ? "" : `?document_id=${encodeURIComponent(documentId)}`;
  const response = await fetch(`${platformGraphBaseUrl}/v1/assignments/${encodeURIComponent(assignmentId)}/handover/document${query}`,
    { headers, credentials: "include" });
  if (!response.ok) {
    await unwrapEnvelope(Promise.resolve({ error: await response.json(), response }));
    return;
  }
  const url = URL.createObjectURL(await response.blob());
  try {
    const anchor = document.createElement("a");
    anchor.href = url; anchor.download = filename;
    document.body.appendChild(anchor); anchor.click(); anchor.remove();
  } finally { URL.revokeObjectURL(url); }
}

type SignPost = (
  path: "/v1/assignments/{assignment_id}/sign",
  init: {
    params: { path: { assignment_id: string } };
    body: SignDraft;
    headers: Record<string, string>;
  },
) => Promise<
  EnvelopedFetchResult<{ handover_record: HandoverRecordRow; assignment: AssignmentRow }>
>;

/** The custodian signs their OWN zimmet (pending_handover → active). Carries
 * `x-idempotency-key` (spec `idempotency: required`) so a double-submit /
 * network retry replays the original handover record instead of appending a
 * duplicate signed artifact (and, for an e-signature method, a duplicate
 * spec-12 signature request). */
export async function signAssignment(
  assignmentId: string,
  draft: SignDraft,
  idempotencyKey: string = crypto.randomUUID(),
): Promise<{ handover_record: HandoverRecordRow; assignment: AssignmentRow }> {
  const post = platformGraph.POST as unknown as SignPost;
  return unwrapEnvelope(
    post("/v1/assignments/{assignment_id}/sign", {
      params: { path: { assignment_id: assignmentId } },
      body: draft,
      headers: idempotencyHeader(idempotencyKey),
    }),
  );
}

type ReturnPost = (
  path: "/v1/assignments/{assignment_id}/return",
  init: {
    params: { path: { assignment_id: string } };
    body: ReturnDraft;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ assignment: AssignmentRow }>>;

/** Returns/ends custody (active → returned). Carries `x-idempotency-key` (spec
 * `idempotency: required`) so a double-submit / network retry replays the
 * original response instead of re-flipping state. */
export async function returnAssignment(
  assignmentId: string,
  draft: ReturnDraft,
  idempotencyKey: string,
): Promise<AssignmentRow> {
  const post = platformGraph.POST as unknown as ReturnPost;
  const result = await unwrapEnvelope(
    post("/v1/assignments/{assignment_id}/return", {
      params: { path: { assignment_id: assignmentId } },
      body: draft,
      headers: idempotencyHeader(idempotencyKey),
    }),
  );
  return result.assignment;
}

type ForceEndPost = (
  path: "/v1/assignments/{assignment_id}/force-end",
  init: {
    params: { path: { assignment_id: string } };
    body: ForceEndDraft;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ assignment: AssignmentRow }>>;

/**
 * Force-end an assignment (administrative terminal exit). For a stuck /
 * abandoned assignment that return can't resolve — the asset returns to in_stock
 * so it is immediately re-assignable. Server-authoritative (manager+); the UI
 * gates the affordance on ASSET_CAP_MANAGE as a convenience.
 */
export async function forceEnd(
  assignmentId: string,
  draft: ForceEndDraft,
  idempotencyKey: string,
): Promise<AssignmentRow> {
  const post = platformGraph.POST as unknown as ForceEndPost;
  const result = await unwrapEnvelope(
    post("/v1/assignments/{assignment_id}/force-end", {
      params: { path: { assignment_id: assignmentId } },
      body: draft,
      headers: idempotencyHeader(idempotencyKey),
    }),
  );
  return result.assignment;
}

type WriteOffPost = (
  path: "/v1/assets/{asset_id}/write-off",
  init: {
    params: { path: { asset_id: string } };
    body: WriteOffDraft;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<WriteOffResult>>;

/**
 * Write off an asset. Resolves to `{ asset, approval_request_id? }`:
 *   * 200 → asset.state === "written_off" (direct, below threshold).
 *   * 202 → approval_request_id present + asset UNCHANGED (pending approval).
 * The caller inspects `approval_request_id` to surface the pending state.
 * Carries `x-idempotency-key` (spec `idempotency: required`) so a double-submit
 * / network retry replays the original response (same approval_request_id, or
 * the same written_off asset) instead of opening a duplicate approval or
 * re-flipping an already-terminal asset.
 */
export async function writeOffAsset(
  assetId: string,
  draft: WriteOffDraft,
  idempotencyKey: string,
): Promise<WriteOffResult> {
  const post = platformGraph.POST as unknown as WriteOffPost;
  return unwrapEnvelope(
    post("/v1/assets/{asset_id}/write-off", {
      params: { path: { asset_id: assetId } },
      body: draft,
      headers: idempotencyHeader(idempotencyKey),
    }),
  );
}

type WriteOffDecisionsGet = (
  path: "/v1/assets/{asset_id}/write-off-decisions",
  init: { params: { path: { asset_id: string } } },
) => Promise<EnvelopedFetchResult<{ items: WriteOffDecisionRow[] }>>;

/**
 * Read the scrap decisions recorded against one asset, newest first.
 * Unpaginated: the route caps the set, because an asset carries one or two of
 * these over its life.
 */
export async function listWriteOffDecisions(
  assetId: string,
): Promise<WriteOffDecisionRow[]> {
  const g = platformGraph.GET as unknown as WriteOffDecisionsGet;
  const result = await unwrapEnvelope(
    g("/v1/assets/{asset_id}/write-off-decisions", {
      params: { path: { asset_id: assetId } },
    }),
  );
  return result.items;
}
